'use client';

import { Check, Monitor, Moon, Palette, Sun } from 'lucide-react';

import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ScrollArea,
  ScrollAreaViewport,
} from '@data-delivery/d3s';
import { useTheme } from './theme-provider';

interface ThemeSwitcherProps {
  variant?: 'default' | 'ghost' | 'outline';
  size?: 'default' | 'sm' | 'lg' | 'icon';
  showLabel?: boolean;
  className?: string;
}

export function ThemeSwitcher({
  variant = 'ghost',
  size = 'icon',
  showLabel = false,
  className,
}: ThemeSwitcherProps) {
  const { config, currentTheme, setTheme, setMode, availableThemes, isLoading } = useTheme();

  if (isLoading) {
    return (
      <Button variant={variant} size={size} className={className} disabled>
        <Monitor className='h-4 w-4' />
        {showLabel && <span className='ml-2'>Loading...</span>}
      </Button>
    );
  }

  const getModeIcon = () =>
    config.mode === 'dark' ? <Moon className='h-3 w-3' /> : <Sun className='h-3 w-3' />;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} className={className}>
          <div className='flex items-center gap-1'>
            <Palette className='h-4 w-4' />
            {getModeIcon()}
          </div>
          {showLabel && (
            <span className='ml-2'>
              {currentTheme.name} ({config.mode})
            </span>
          )}
          <span className='sr-only'>Toggle theme and mode</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-56'>
        <DropdownMenuLabel>Appearance Mode</DropdownMenuLabel>
        <DropdownMenuItem
          onClick={() => {
            setMode('light');
          }}
        >
          <Sun className='mr-2 h-4 w-4' />
          Light
          {config.mode === 'light' && <Check className='ml-auto h-4 w-4' />}
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => {
            setMode('dark');
          }}
        >
          <Moon className='mr-2 h-4 w-4' />
          Dark
          {config.mode === 'dark' && <Check className='ml-auto h-4 w-4' />}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <DropdownMenuLabel>Color Theme</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <ScrollArea className='h-[200px]'>
          <ScrollAreaViewport>
            {availableThemes.map(theme => (
              <DropdownMenuItem
                key={theme.id}
                onClick={() => {
                  setTheme(theme.id);
                }}
                className='flex items-center justify-between cursor-pointer'
              >
                <div className='flex items-center'>
                  <div className='flex flex-col'>
                    <div className='flex items-center gap-2 w-full'>
                      <span>{theme.name}</span>
                      <div className='flex gap-0.5 ml-auto justify-end'>
                        <div
                          className='w-3 h-3 rounded-full border border-border/50'
                          style={{
                            backgroundColor: `hsl(${theme.colors[config.mode].primary})`,
                          }}
                          title='Primary'
                        />
                        <div
                          className='w-3 h-3 rounded-full border border-border/50'
                          style={{
                            backgroundColor: `hsl(${theme.colors[config.mode].secondary})`,
                          }}
                          title='Secondary'
                        />
                        <div
                          className='w-3 h-3 rounded-full border border-border/50'
                          style={{
                            backgroundColor: `hsl(${theme.colors[config.mode].accent})`,
                          }}
                          title='Accent'
                        />
                        <div
                          className='w-3 h-3 rounded-full border border-border/50'
                          style={{
                            backgroundColor: `hsl(${theme.colors[config.mode]['color-1']})`,
                          }}
                          title='Color 1'
                        />
                        <div
                          className='w-3 h-3 rounded-full border border-border/50'
                          style={{
                            backgroundColor: `hsl(${theme.colors[config.mode]['color-2']})`,
                          }}
                          title='Color 2'
                        />
                      </div>
                    </div>
                    {theme.description && (
                      <span className='text-xs text-muted-foreground'>{theme.description}</span>
                    )}
                  </div>
                </div>
                {currentTheme.id === theme.id && <Check className='h-4 w-4' />}
              </DropdownMenuItem>
            ))}
          </ScrollAreaViewport>
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
